import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell
} from 'recharts';
import { api } from '../services/api';
import { usePortfolio } from '../context/PortfolioContext';
import './BacktestPage.css';

const BacktestPage = ({ selectedFunds, onBack }) => {
  const { optimizationResults } = usePortfolio();

  const [timeRange, setTimeRange] = useState({ start: null, end: null });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('performance');

  // Initialize date range
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setFullYear(start.getFullYear() - 5); // Default 5 years

    setTimeRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  }, []);

  // Extract weights from optimization results
  const getWeightsFromResults = () => {
    if (!optimizationResults?.weights) return {};
    const weights = {};
    Object.entries(optimizationResults.weights).forEach(([code, data]) => {
      weights[code] = typeof data === 'object' ? data.weight : data;
    });
    return weights;
  };

  const runBacktest = async () => {
    if (!selectedFunds || selectedFunds.length === 0) {
      setError('No funds selected. Please go back and select funds.');
      return;
    }

    const weights = getWeightsFromResults();
    if (Object.keys(weights).length === 0) {
      setError('No portfolio weights available. Please run optimization first.');
      return;
    }

    // Extract scheme codes
    let schemeCodes = selectedFunds;
    if (selectedFunds.length > 0 && typeof selectedFunds[0] === 'object') {
      schemeCodes = selectedFunds.map(f => f.schemeCode || f.scheme_code || f);
    }

    setLoading(true);
    setError(null);

    try {
      const requestData = {
        scheme_codes: schemeCodes,
        weights: weights,
        start_date: timeRange.start,
        end_date: timeRange.end
      };

      const response = await api.runBacktest(requestData);
      setResults(response.data);
    } catch (err) {
      console.error('Backtest error:', err);
      let errorMessage;
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. The backtest is taking longer than expected. Please try with a shorter date range or fewer funds.';
      } else {
        errorMessage = err.response?.data?.detail || err.message || String(err);
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return value.toFixed(3);
  };

  // Prepare cumulative returns chart data
  const prepareCumulativeData = () => {
    if (!results?.cumulative_returns) return [];
    return results.cumulative_returns.map(item => ({
      ...item,
      date: item.date.split('T')[0],
      portfolio: ((item.portfolio - 1) * 100),
      equal_weight: ((item.equal_weight - 1) * 100)
    }));
  };

  // Prepare drawdown chart data
  const prepareDrawdownData = () => {
    if (!results?.drawdown_series) return [];
    return results.drawdown_series.map(item => ({
      date: item.date.split('T')[0],
      drawdown: item.drawdown * 100
    }));
  };

  // Prepare sensitivity analysis data for chart
  const prepareSensitivityData = () => {
    if (!results?.sensitivity_analysis?.all_alternatives) return [];

    // Get systematic variations only for cleaner chart
    const systematic = results.sensitivity_analysis.all_alternatives
      .filter(alt => alt.type === 'systematic')
      .slice(0, 12);

    return systematic.map(alt => ({
      name: alt.description.length > 15 ? alt.description.substring(0, 15) + '...' : alt.description,
      fullName: alt.description,
      sharpe: alt.sharpe_ratio,
      return: alt.annual_return * 100,
      sharpe_diff: alt.sharpe_diff,
      return_diff: alt.return_diff * 100
    }));
  };

  const calculateDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
  };

  const totalDays = timeRange.start && timeRange.end ?
    calculateDays(timeRange.start, timeRange.end) : 0;

  // Custom tooltip for cumulative returns
  const CumulativeTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-date">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value.toFixed(2)}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="backtest-page">
      <div className="page-header">
        <h1>Portfolio Backtest</h1>
        <p>Analyze historical performance of your optimized portfolio</p>
      </div>

      {/* Configuration Section */}
      <div className="config-section">
        <div className="config-card">
          <h3>Backtest Configuration</h3>

          <div className="date-inputs">
            <div className="date-input-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={timeRange.start || ''}
                onChange={(e) => setTimeRange({...timeRange, start: e.target.value})}
              />
            </div>

            <div className="date-input-group">
              <label>End Date:</label>
              <input
                type="date"
                value={timeRange.end || ''}
                onChange={(e) => setTimeRange({...timeRange, end: e.target.value})}
              />
            </div>
          </div>

          <div className="period-info">
            <div className="info-item">
              <span className="label">Total Period:</span>
              <span className="value">{totalDays} days (~{(totalDays / 365).toFixed(1)} years)</span>
            </div>
            <div className="info-item">
              <span className="label">Funds:</span>
              <span className="value">{selectedFunds?.length || 0}</span>
            </div>
          </div>

          <button
            className="run-backtest-btn"
            onClick={runBacktest}
            disabled={loading || !timeRange.start || !timeRange.end}
          >
            {loading ? 'Running Backtest...' : 'Run Backtest'}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Results Section */}
      {results && (
        <div className="results-section">
          {/* Tabs */}
          <div className="results-tabs">
            <button
              className={`tab ${activeTab === 'performance' ? 'active' : ''}`}
              onClick={() => setActiveTab('performance')}
            >
              Performance
            </button>
            <button
              className={`tab ${activeTab === 'comparison' ? 'active' : ''}`}
              onClick={() => setActiveTab('comparison')}
            >
              Comparison
            </button>
            <button
              className={`tab ${activeTab === 'sensitivity' ? 'active' : ''}`}
              onClick={() => setActiveTab('sensitivity')}
            >
              Sensitivity Analysis
            </button>
            <button
              className={`tab ${activeTab === 'risk' ? 'active' : ''}`}
              onClick={() => setActiveTab('risk')}
            >
              Risk Analysis
            </button>
          </div>

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <>
              {/* Key Metrics Summary */}
              <div className="metrics-summary">
                <h2>Portfolio Performance Summary</h2>
                <div className="metrics-grid">
                  <div className="metric-card highlight">
                    <span className="metric-label">Total Return</span>
                    <span className="metric-value positive">
                      {formatPercentage(results.portfolio_metrics?.total_return)}
                    </span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">CAGR</span>
                    <span className="metric-value">
                      {formatPercentage(results.portfolio_metrics?.cagr)}
                    </span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Sharpe Ratio</span>
                    <span className="metric-value">
                      {formatNumber(results.portfolio_metrics?.sharpe_ratio)}
                    </span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Volatility</span>
                    <span className="metric-value">
                      {formatPercentage(results.portfolio_metrics?.volatility)}
                    </span>
                  </div>
                  <div className="metric-card negative">
                    <span className="metric-label">Max Drawdown</span>
                    <span className="metric-value">
                      {formatPercentage(results.portfolio_metrics?.max_drawdown)}
                    </span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-label">Sortino Ratio</span>
                    <span className="metric-value">
                      {formatNumber(results.portfolio_metrics?.sortino_ratio)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Cumulative Returns Chart */}
              <div className="chart-card">
                <h3>Cumulative Returns</h3>
                <p className="chart-description">
                  Portfolio growth over the backtest period vs equal-weight benchmark
                </p>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={prepareCumulativeData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <defs>
                      <linearGradient id="colorPortfolio" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorBenchmark" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#64748b" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#64748b" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.substring(0, 7)}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `${value.toFixed(0)}%`}
                      label={{ value: 'Return (%)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip content={<CumulativeTooltip />} />
                    <Legend />
                    <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                    <Area
                      type="monotone"
                      dataKey="portfolio"
                      name="Optimized Portfolio"
                      stroke="#2563eb"
                      fill="url(#colorPortfolio)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="equal_weight"
                      name="Equal Weight"
                      stroke="#64748b"
                      fill="url(#colorBenchmark)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {/* Comparison Tab */}
          {activeTab === 'comparison' && (
            <>
              {/* Portfolio vs Benchmark */}
              <div className="comparison-card">
                <h3>Optimized Portfolio vs Equal-Weight Benchmark</h3>
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Optimized Portfolio</th>
                      <th>Equal Weight</th>
                      <th>Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Total Return</td>
                      <td>{formatPercentage(results.portfolio_metrics?.total_return)}</td>
                      <td>{formatPercentage(results.benchmark_metrics?.total_return)}</td>
                      <td className={results.portfolio_metrics?.total_return > results.benchmark_metrics?.total_return ? 'positive' : 'negative'}>
                        {formatPercentage((results.portfolio_metrics?.total_return || 0) - (results.benchmark_metrics?.total_return || 0))}
                      </td>
                    </tr>
                    <tr>
                      <td>CAGR</td>
                      <td>{formatPercentage(results.portfolio_metrics?.cagr)}</td>
                      <td>{formatPercentage(results.benchmark_metrics?.cagr)}</td>
                      <td className={results.portfolio_metrics?.cagr > results.benchmark_metrics?.cagr ? 'positive' : 'negative'}>
                        {formatPercentage((results.portfolio_metrics?.cagr || 0) - (results.benchmark_metrics?.cagr || 0))}
                      </td>
                    </tr>
                    <tr>
                      <td>Sharpe Ratio</td>
                      <td>{formatNumber(results.portfolio_metrics?.sharpe_ratio)}</td>
                      <td>{formatNumber(results.benchmark_metrics?.sharpe_ratio)}</td>
                      <td className={results.portfolio_metrics?.sharpe_ratio > results.benchmark_metrics?.sharpe_ratio ? 'positive' : 'negative'}>
                        {formatNumber((results.portfolio_metrics?.sharpe_ratio || 0) - (results.benchmark_metrics?.sharpe_ratio || 0))}
                      </td>
                    </tr>
                    <tr>
                      <td>Volatility</td>
                      <td>{formatPercentage(results.portfolio_metrics?.volatility)}</td>
                      <td>{formatPercentage(results.benchmark_metrics?.volatility)}</td>
                      <td className={results.portfolio_metrics?.volatility < results.benchmark_metrics?.volatility ? 'positive' : 'negative'}>
                        {formatPercentage((results.portfolio_metrics?.volatility || 0) - (results.benchmark_metrics?.volatility || 0))}
                      </td>
                    </tr>
                    <tr>
                      <td>Max Drawdown</td>
                      <td>{formatPercentage(results.portfolio_metrics?.max_drawdown)}</td>
                      <td>{formatPercentage(results.benchmark_metrics?.max_drawdown)}</td>
                      <td className={Math.abs(results.portfolio_metrics?.max_drawdown || 0) < Math.abs(results.benchmark_metrics?.max_drawdown || 0) ? 'positive' : 'negative'}>
                        {formatPercentage((results.portfolio_metrics?.max_drawdown || 0) - (results.benchmark_metrics?.max_drawdown || 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Individual Fund Performance */}
              <div className="fund-performance-card">
                <h3>Individual Fund Performance</h3>
                <table className="fund-table">
                  <thead>
                    <tr>
                      <th>Fund</th>
                      <th>Weight</th>
                      <th>CAGR</th>
                      <th>Volatility</th>
                      <th>Sharpe</th>
                      <th>Max Drawdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(results.individual_fund_performance || {}).map(([code, metrics]) => (
                      <tr key={code}>
                        <td title={results.fund_names?.[code] || code}>
                          {(results.fund_names?.[code] || code).substring(0, 30)}
                          {(results.fund_names?.[code] || code).length > 30 ? '...' : ''}
                        </td>
                        <td>{formatPercentage(results.weights_used?.[code])}</td>
                        <td>{formatPercentage(metrics.annual_return)}</td>
                        <td>{formatPercentage(metrics.annual_volatility)}</td>
                        <td>{formatNumber(metrics.sharpe_ratio)}</td>
                        <td>{formatPercentage(metrics.max_drawdown)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Sensitivity Analysis Tab */}
          {activeTab === 'sensitivity' && results.sensitivity_analysis && (
            <>
              <div className="sensitivity-summary">
                <h3>Allocation Sensitivity Analysis</h3>
                <p className="section-description">
                  Tests how portfolio performance changes with different allocation variations (±5%, ±10%, ±15%)
                </p>

                <div className="sensitivity-metrics">
                  <div className={`sensitivity-card ${results.sensitivity_analysis.sensitivity_summary?.is_robust ? 'robust' : 'not-robust'}`}>
                    <span className="card-label">Robustness</span>
                    <span className="card-value">
                      {results.sensitivity_analysis.sensitivity_summary?.is_robust ? 'Robust' : 'Sensitive'}
                    </span>
                    <span className="card-description">
                      {results.sensitivity_analysis.sensitivity_summary?.n_better_alternatives || 0} alternatives
                      outperform by &gt;0.1 Sharpe
                    </span>
                  </div>
                  <div className="sensitivity-card">
                    <span className="card-label">Sharpe Range</span>
                    <span className="card-value">
                      {formatNumber(results.sensitivity_analysis.sensitivity_summary?.sharpe_range?.[0])} - {formatNumber(results.sensitivity_analysis.sensitivity_summary?.sharpe_range?.[1])}
                    </span>
                  </div>
                  <div className="sensitivity-card">
                    <span className="card-label">Robustness Score</span>
                    <span className="card-value">
                      {(results.sensitivity_analysis.robustness_score * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Best Alternative */}
              {results.sensitivity_analysis.best_alternative &&
               results.sensitivity_analysis.best_alternative.sharpe_diff > 0.05 && (
                <div className="best-alternative-card">
                  <h4>Potential Improvement Found</h4>
                  <p>
                    <strong>{results.sensitivity_analysis.best_alternative.description}</strong> achieves
                    Sharpe {formatNumber(results.sensitivity_analysis.best_alternative.sharpe_ratio)}
                    (vs {formatNumber(results.sensitivity_analysis.base_metrics?.sharpe_ratio)} base)
                  </p>
                </div>
              )}

              {/* Sensitivity Chart */}
              <div className="chart-card">
                <h3>Performance Variations by Allocation Change</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={prepareSensitivityData()} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Sharpe Ratio', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="custom-tooltip">
                              <p><strong>{data.fullName}</strong></p>
                              <p>Sharpe: {formatNumber(data.sharpe)}</p>
                              <p>Return: {data.return.toFixed(2)}%</p>
                              <p>Sharpe Diff: {data.sharpe_diff > 0 ? '+' : ''}{formatNumber(data.sharpe_diff)}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <ReferenceLine
                      y={results.sensitivity_analysis.base_metrics?.sharpe_ratio || 0}
                      stroke="#2563eb"
                      strokeDasharray="5 5"
                      label={{ value: 'Base', position: 'right', fill: '#2563eb' }}
                    />
                    <Bar dataKey="sharpe" name="Sharpe Ratio">
                      {prepareSensitivityData().map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.sharpe_diff > 0 ? '#22c55e' : entry.sharpe_diff < -0.1 ? '#ef4444' : '#64748b'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Alternatives Table */}
              <div className="alternatives-table-card">
                <h3>Top Alternative Allocations</h3>
                <table className="alternatives-table">
                  <thead>
                    <tr>
                      <th>Variation</th>
                      <th>Sharpe</th>
                      <th>CAGR</th>
                      <th>Volatility</th>
                      <th>vs Base Sharpe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="base-row">
                      <td><strong>Base (Optimized)</strong></td>
                      <td>{formatNumber(results.sensitivity_analysis.base_metrics?.sharpe_ratio)}</td>
                      <td>{formatPercentage(results.sensitivity_analysis.base_metrics?.annual_return)}</td>
                      <td>{formatPercentage(results.sensitivity_analysis.base_metrics?.annual_volatility)}</td>
                      <td>-</td>
                    </tr>
                    {results.sensitivity_analysis.all_alternatives?.slice(0, 10).map((alt, idx) => (
                      <tr key={idx} className={alt.sharpe_diff > 0.05 ? 'better' : alt.sharpe_diff < -0.1 ? 'worse' : ''}>
                        <td>{alt.description}</td>
                        <td>{formatNumber(alt.sharpe_ratio)}</td>
                        <td>{formatPercentage(alt.annual_return)}</td>
                        <td>{formatPercentage(alt.annual_volatility)}</td>
                        <td className={alt.sharpe_diff > 0 ? 'positive' : 'negative'}>
                          {alt.sharpe_diff > 0 ? '+' : ''}{formatNumber(alt.sharpe_diff)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Risk Analysis Tab */}
          {activeTab === 'risk' && (
            <>
              {/* Drawdown Chart */}
              <div className="chart-card">
                <h3>Drawdown Over Time</h3>
                <p className="chart-description">
                  Shows portfolio decline from peak value - lower (more negative) is worse
                </p>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={prepareDrawdownData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <defs>
                      <linearGradient id="colorDrawdown" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.substring(0, 7)}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `${value.toFixed(0)}%`}
                      domain={['dataMin', 0]}
                    />
                    <Tooltip
                      formatter={(value) => [`${value.toFixed(2)}%`, 'Drawdown']}
                    />
                    <ReferenceLine y={0} stroke="#94a3b8" />
                    <Area
                      type="monotone"
                      dataKey="drawdown"
                      stroke="#ef4444"
                      fill="url(#colorDrawdown)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Risk Metrics */}
              <div className="risk-metrics-card">
                <h3>Risk Metrics Summary</h3>
                <div className="risk-grid">
                  <div className="risk-item">
                    <span className="risk-label">Maximum Drawdown</span>
                    <span className="risk-value negative">
                      {formatPercentage(results.portfolio_metrics?.max_drawdown)}
                    </span>
                    <span className="risk-description">
                      Largest peak-to-trough decline
                    </span>
                  </div>
                  <div className="risk-item">
                    <span className="risk-label">Volatility (Annual)</span>
                    <span className="risk-value">
                      {formatPercentage(results.portfolio_metrics?.volatility)}
                    </span>
                    <span className="risk-description">
                      Standard deviation of returns
                    </span>
                  </div>
                  <div className="risk-item">
                    <span className="risk-label">Calmar Ratio</span>
                    <span className="risk-value">
                      {formatNumber(results.portfolio_metrics?.calmar_ratio)}
                    </span>
                    <span className="risk-description">
                      CAGR / Max Drawdown
                    </span>
                  </div>
                  <div className="risk-item">
                    <span className="risk-label">Sortino Ratio</span>
                    <span className="risk-value">
                      {formatNumber(results.portfolio_metrics?.sortino_ratio)}
                    </span>
                    <span className="risk-description">
                      Return per unit of downside risk
                    </span>
                  </div>
                </div>
              </div>

              {/* Period Info */}
              <div className="period-info-card">
                <h3>Backtest Period</h3>
                <div className="period-details">
                  <div className="period-item">
                    <span>Start Date:</span>
                    <span>{results.period?.start?.split('T')[0]}</span>
                  </div>
                  <div className="period-item">
                    <span>End Date:</span>
                    <span>{results.period?.end?.split('T')[0]}</span>
                  </div>
                  <div className="period-item">
                    <span>Trading Days:</span>
                    <span>{results.period?.trading_days}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Back Button */}
      <div className="page-actions">
        <button className="btn-back" onClick={onBack}>
          ← Back to Results
        </button>
      </div>
    </div>
  );
};

export default BacktestPage;
